import type { AssessmentTrendSeries } from '@cartwheel/api/v1'
import { Chart } from 'chart.js/auto'
import { useEffect, useRef } from 'react'

interface Props {
  series: AssessmentTrendSeries[]
}

const LINE_COLORS = ['#26544f', '#996d1d', '#a12d3b', '#005d92', '#b1a5f7', '#38805d']
const LINE_DASHES: number[][] = [[], [7, 4], [2, 4], [9, 4, 2, 4], [1, 5], [12, 5]]

function formatShortDate(iso: string | null | undefined): string {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

interface OccasionDomainEntry {
  occasionId: string
  completedAt: string | null | undefined
}

function buildOccasionDomain(series: AssessmentTrendSeries[]): OccasionDomainEntry[] {
  const byOccasion = new Map<string, OccasionDomainEntry>()
  for (const subscale of series) {
    for (const point of subscale.points) {
      if (!byOccasion.has(point.occasion_id)) {
        byOccasion.set(point.occasion_id, {
          occasionId: point.occasion_id,
          completedAt: point.completed_at,
        })
      }
    }
  }
  return [...byOccasion.values()].sort((a, b) => {
    const aTime = a.completedAt ? new Date(a.completedAt).getTime() : 0
    const bTime = b.completedAt ? new Date(b.completedAt).getTime() : 0
    return aTime - bTime
  })
}

export function AssessmentTrendChart({ series }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const populated = series.filter((subscale) => subscale.points.length > 0)

  useEffect(() => {
    const canvas = canvasRef.current
    const active = series.filter((subscale) => subscale.points.length > 0)
    if (!canvas || active.length === 0) return

    const domain = buildOccasionDomain(active)
    const indexByOccasion = new Map(domain.map((entry, index) => [entry.occasionId, index]))

    const datasets = active.map((subscale, subscaleIndex) => {
      const color = LINE_COLORS[subscaleIndex % LINE_COLORS.length]
      const data: (number | null)[] = domain.map(() => null)
      for (const point of subscale.points) {
        const index = indexByOccasion.get(point.occasion_id)
        if (index != null) data[index] = point.raw_score ?? null
      }
      return {
        label: subscale.label ?? subscale.subscale_key ?? '',
        data,
        borderColor: color,
        backgroundColor: color,
        borderDash: LINE_DASHES[subscaleIndex % LINE_DASHES.length],
        spanGaps: true,
        tension: 0,
        pointRadius: 4,
      }
    })

    const chart = new Chart(canvas, {
      type: 'line',
      data: { labels: domain.map((entry) => formatShortDate(entry.completedAt)), datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: {
            position: 'bottom',
            labels: { usePointStyle: true, pointStyle: 'circle', boxWidth: 8 },
          },
        },
        scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
      },
    })

    return () => chart.destroy()
  }, [series])

  if (populated.length === 0) return null

  return (
    <div className="h-60 max-w-2xl">
      <canvas ref={canvasRef} role="img" aria-label="Subscale score trend over time" />
    </div>
  )
}
